import { useQuery } from '@tanstack/react-query';
import { equipmentService } from '@/services/equipment.service';
import type { EquipmentWorkflowGroup } from '@/services/equipment.service';

/**
 * D-106 — the operational summary, moved here from the Equipment page.
 *
 * **Population note (approved decision A(a)).** These counts are
 * **EQUIPMENT-based**: they come from `GET /equipment`, the same Stage C
 * aggregation the Equipment page used, driven by D-102's derived
 * `workflowGroup`. The table below this panel lists **inward records**. Today
 * the primary tenant is 1:1 so the two agree, but the schema permits equipment
 * with no inward and equipment with several, so the two populations are not
 * identical in principle.
 *
 * That mismatch is deliberately NOT "fixed" by building a second, inward-based
 * aggregation — that would be a second workflow counting system. Instead the
 * heading says **Equipment Summary**, so the number is never mistaken for a
 * count of inward rows.
 *
 * Nothing here is stored, and no workflow is derived locally.
 */

interface Props {
  /** Active customer scope, or '' for all. */
  customerId: string;
  /** Active workflow-group scope, or '' for all. */
  workflowGroup: EquipmentWorkflowGroup | '';
  /** Drill into one organisation — same handler the page uses for its filter. */
  onSelectCustomer: (customerId: string) => void;
}

const CARD_TONE: Record<string, string> = {
  Total: 'text-foreground',
  Pending: 'text-slate-600',
  'In Progress': 'text-amber-700',
  Completed: 'text-green-700',
};

const organisationOf = (row: {
  customerOrganizationName: string | null;
  customerName: string | null;
}) => row.customerOrganizationName ?? row.customerName;

export function InwardOperationalSummary({ customerId, workflowGroup, onSelectCustomer }: Props) {
  const { data } = useQuery({
    queryKey: ['inward-operational-summary', customerId, workflowGroup],
    queryFn: () =>
      equipmentService.list({
        page: 1,
        // Only the summary is wanted, never the rows.
        pageSize: 1,
        customerId: customerId || undefined,
        workflowGroup: workflowGroup || undefined,
      }),
  });

  const summary = data?.summary;
  const cards: ReadonlyArray<[string, number | null | undefined]> = [
    ['Total', summary?.total],
    ['Pending', summary?.pending],
    ['In Progress', summary?.inProgress],
    ['Completed', summary?.completed],
  ];

  return (
    <>
      <div
        role="group"
        aria-label="Equipment summary"
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      >
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-border bg-card px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${CARD_TONE[label]}`}>
              {value ?? '—'}
            </p>
          </div>
        ))}
      </div>

      {data?.summary && !data.summary.workflowCountsAvailable ? (
        <p className="rounded-lg border border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          Too many records to break down by workflow. Narrow the filters to see the split.
        </p>
      ) : null}

      {/*
        D-103's rule is preserved: the breakdown is hidden once a customer is
        selected, because the cards above already describe exactly that
        organisation. The dropdown on the page keeps its full option list.
      */}
      {!customerId && (data?.customerSummary?.length ?? 0) > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[560px] text-sm">
            <caption className="px-4 pt-3 text-left text-xs font-medium text-muted-foreground">
              Customer Summary
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-4 py-2 font-medium text-foreground">Organisation</th>
                <th className="px-4 py-2 text-right font-medium text-foreground">Total</th>
                <th className="px-4 py-2 text-right font-medium text-foreground">Pending</th>
                <th className="px-4 py-2 text-right font-medium text-foreground">In Progress</th>
                <th className="px-4 py-2 text-right font-medium text-foreground">Completed</th>
              </tr>
            </thead>
            <tbody>
              {(data?.customerSummary ?? []).map((row) => (
                <tr
                  key={row.customerId ?? 'company-inventory'}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-4 py-2">
                    {row.customerId ? (
                      <button
                        type="button"
                        onClick={() => onSelectCustomer(row.customerId ?? '')}
                        className="truncate text-left text-foreground underline-offset-4 hover:text-primary hover:underline"
                      >
                        {organisationOf(row)}
                      </button>
                    ) : (
                      // Company Inventory is a category, not a customer.
                      <span className="text-xs font-medium text-primary/70">Company Inventory</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-foreground">{row.total}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {row.pending}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {row.inProgress}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {row.completed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
